import { useRef, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  makeStyles,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Theme,
  Typography,
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import {
  uploadContextFile,
  uploadFileVersion,
  type ProjectsApiOptions,
} from '../../api/projects';
import { type ContextFileEntry, type ContextFileTypeTag } from './types';

// Keep in sync with backend allowedExtensions whitelist
const ALLOWED_EXTENSIONS = new Set([
  '.a2l',
  '.arxml',
  '.c',
  '.capl',
  '.cpp',
  '.csv',
  '.dbc',
  '.docx',
  '.fibex',
  '.h',
  '.html',
  '.json',
  '.ldf',
  '.m',
  '.md',
  '.odx',
  '.pdf',
  '.py',
  '.reqif',
  '.reqifz',
  '.rst',
  '.txt',
  '.vbs',
  '.xlsx',
  '.xml',
  '.yaml',
]);

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB — temporary direct-upload limit

const TYPE_TAG_OPTIONS: { value: ContextFileTypeTag; labelKey: string }[] = [
  {
    value: 'SYS2_REQUIREMENT',
    labelKey: 'uploadDialog.typeTags.SYS2_REQUIREMENT',
  },
  {
    value: 'SYS5_TEST_SCRIPT',
    labelKey: 'uploadDialog.typeTags.SYS5_TEST_SCRIPT',
  },
  { value: 'API_DESCRIPTOR', labelKey: 'uploadDialog.typeTags.API_DESCRIPTOR' },
  { value: 'GENERAL', labelKey: 'uploadDialog.typeTags.GENERAL' },
];

const useStyles = makeStyles((theme: Theme) => ({
  field: {
    marginTop: theme.spacing(2),
  },
  progressBox: {
    marginTop: theme.spacing(2),
  },
  errorText: {
    color: theme.palette.error.main,
    marginTop: theme.spacing(1),
  },
  fileNameText: {
    marginTop: theme.spacing(1),
    color: theme.palette.text.secondary,
  },
}));

type Props = {
  open: boolean;
  onClose: () => void;
  projectId: string;
  apiOptions: ProjectsApiOptions;
  isPreviewMode: boolean;
  onUploaded: (file: ContextFileEntry) => void;
  /** When set, upload a new version of this existing file instead of a new file. */
  targetFile?: ContextFileEntry | null;
};

export function UploadContextFileDialog({
  open,
  onClose,
  projectId,
  apiOptions,
  isPreviewMode,
  onUploaded,
  targetFile,
}: Props) {
  const classes = useStyles();
  const { t } = useTranslation('projects');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const isNewVersion = !!targetFile;

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [typeTag, setTypeTag] = useState<ContextFileTypeTag>('GENERAL');
  const [progress, setProgress] = useState<number | null>(null);
  const [validationError, setValidationError] = useState<string | undefined>();
  const [uploadError, setUploadError] = useState<string | undefined>();

  const reset = () => {
    setSelectedFile(null);
    setTypeTag('GENERAL');
    setProgress(null);
    setValidationError(undefined);
    setUploadError(undefined);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const validateFile = (file: File): string | undefined => {
    if (file.size > MAX_FILE_SIZE) {
      return t('uploadDialog.errorFileTooLarge');
    }
    const ext = `.${file.name.split('.').pop()?.toLowerCase() ?? ''}`;
    if (!ALLOWED_EXTENSIONS.has(ext)) {
      return t('uploadDialog.errorInvalidType');
    }
    return undefined;
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;
    setSelectedFile(file);
    setValidationError(file ? validateFile(file) : undefined);
    setUploadError(undefined);
  };

  const handleUpload = async () => {
    if (!selectedFile) return;
    const err = validateFile(selectedFile);
    if (err) {
      setValidationError(err);
      return;
    }

    setProgress(0);
    setUploadError(undefined);

    try {
      if (isPreviewMode) {
        for (let p = 0; p <= 100; p += 20) {
          setProgress(p);
          await new Promise(res => setTimeout(res, 100)); // eslint-disable-line no-promise-executor-return
        }
        if (isNewVersion && targetFile) {
          onUploaded({
            ...targetFile,
            status: 'PENDING',
            version: targetFile.version + 1,
          });
        } else {
          const mockFile: ContextFileEntry = {
            fileId: `preview-${Date.now()}`,
            displayName: selectedFile.name,
            fileTypeTag: typeTag,
            status: 'PENDING',
            source: 'project',
            version: 1,
            uploader: { userId: 0, displayName: t('members.selfName') },
            isIncluded: true,
            errorReport: null,
          };
          onUploaded(mockFile);
        }
      } else if (isNewVersion && targetFile) {
        const result = await uploadFileVersion(
          apiOptions,
          projectId,
          targetFile.fileId,
          selectedFile,
          pct => setProgress(pct),
        );
        onUploaded({
          ...targetFile,
          status: result.status,
          version: result.versionNumber,
        });
      } else {
        const result = await uploadContextFile(
          apiOptions,
          projectId,
          selectedFile,
          typeTag,
          pct => setProgress(pct),
        );
        const newFile: ContextFileEntry = {
          fileId: result.fileId,
          displayName: selectedFile.name,
          fileTypeTag: typeTag,
          status: result.status,
          source: 'project',
          version: 1,
          uploader: null,
          isIncluded: true,
          errorReport: null,
        };
        onUploaded(newFile);
      }
      handleClose();
    } catch {
      setUploadError(
        t('uploadDialog.uploadError', {
          defaultValue: 'Upload failed. Please try again.',
        }),
      );
      setProgress(null);
    }
  };

  const isUploading = progress !== null;
  const canSubmit = !!selectedFile && !validationError && !isUploading;

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {isNewVersion
          ? t('uploadDialog.newVersionTitle', { name: targetFile?.displayName })
          : t('uploadDialog.title')}
      </DialogTitle>
      <DialogContent>
        <Button variant="outlined" component="label" fullWidth>
          {t('uploadDialog.fileLabel')}
          <input
            ref={fileInputRef}
            type="file"
            hidden
            onChange={handleFileChange}
          />
        </Button>
        {selectedFile && (
          <Typography variant="body2" className={classes.fileNameText}>
            {selectedFile.name}
          </Typography>
        )}
        {validationError && (
          <Typography variant="body2" className={classes.errorText}>
            {validationError}
          </Typography>
        )}

        {!isNewVersion && (
          <FormControl fullWidth variant="outlined" className={classes.field}>
            <InputLabel>{t('uploadDialog.typeTagLabel')}</InputLabel>
            <Select
              value={typeTag}
              onChange={e => setTypeTag(e.target.value as ContextFileTypeTag)}
              label={t('uploadDialog.typeTagLabel')}
            >
              {TYPE_TAG_OPTIONS.map(opt => (
                <MenuItem key={opt.value} value={opt.value}>
                  {t(opt.labelKey)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {isUploading && (
          <Box className={classes.progressBox}>
            <Typography variant="body2">
              {t('uploadDialog.progress', { percent: progress })}
            </Typography>
            <LinearProgress variant="determinate" value={progress ?? 0} />
          </Box>
        )}

        {uploadError && (
          <Typography variant="body2" className={classes.errorText}>
            {uploadError}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isUploading}>
          {t('actions.cancel')}
        </Button>
        <Button
          color="primary"
          variant="contained"
          disabled={!canSubmit}
          onClick={handleUpload}
        >
          {t('uploadDialog.uploadButton')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
